import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Top from '../pages/Top';
import Bottom from '../pages/Bottom';

/**
 * 화면에 필요한 정보(router state)가 없을 때 보여주는 안내.
 *
 * 이 앱의 여러 화면은 `useLocation().state` 로 '어떤 항목인지'를 넘겨받는다.
 * 그 값은 앱을 백그라운드로 보냈다가 OS 가 WebView 를 되살릴 때, 새로고침,
 * 딥링크로 들어올 때 사라진다. `serve -s build` 라 URL 은 살아나므로 화면은
 * 뜨는데 내용이 없다.
 *
 * 예전에는 그런 경우 `return null` 로 아무것도 그리지 않아 **완전한 백지**가 됐다.
 * 하단 탭까지 사라져서 앱 안에서 빠져나갈 방법이 없었다.
 * 여기서는 무슨 일인지 알려주고 되돌아갈 버튼과 하단 탭을 함께 준다.
 */
const StateMissingFallback = ({
  title,
  message = '화면 정보를 불러오지 못했습니다.\n다시 시도해 주세요.',
  actionLabel,
  to,
}: {
  /** 상단 제목 */
  title: string;
  /** 안내 문구 (줄바꿈 가능) */
  message?: string;
  /** 버튼 문구 */
  actionLabel: string;
  /** 버튼을 눌렀을 때 갈 경로 */
  to: string;
}) => {
  const navigate = useNavigate();

  return (
    <Stack
      sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }}
      spacing={6}
    >
      <Top title={title} />
      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 400 }}>
        <Typography
          sx={{ fontSize: 16, color: '#555', textAlign: 'center', mt: 5, whiteSpace: 'pre-line', lineHeight: 1.6 }}
        >
          {message}
        </Typography>
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(to, { replace: true })}
            sx={{
              color: '#EF8322',
              borderColor: '#EF8322',
              borderRadius: '10px',
              fontWeight: 700,
              px: 3,
              ':hover': { borderColor: '#EF8322', backgroundColor: '#fff6ef' },
            }}
          >
            {actionLabel}
          </Button>
        </Stack>
      </Grid>
      <Bottom />
    </Stack>
  );
};

export default StateMissingFallback;
